/**
 * Firebase — initialisation unique (Auth + Realtime Database + App Check).
 *
 * Config via variables d'environnement (REACT_APP_FIREBASE_*) : projet de dev et projet
 * de prod sans toucher au code. Sans config, l'app démarre (écran de login) mais l'auth
 * échoue avec un message explicite.
 */
import { initializeApp, getApps, getApp } from "firebase/app";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import { getDatabase, connectDatabaseEmulator } from "firebase/database";
import { initializeAppCheck, ReCaptchaV3Provider, getToken, AppCheck } from "firebase/app-check";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "",
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL || "",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "",
};

export const isFirebaseConfigured = Boolean(firebaseConfig.apiKey && firebaseConfig.projectId);

if (!isFirebaseConfigured) {
  // eslint-disable-next-line no-console
  console.warn("[firebase] Configuration absente : copiez .env.example vers .env et renseignez REACT_APP_FIREBASE_*");
}

const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

export const auth = getAuth(app);
auth.languageCode = "fr"; // e-mails Firebase (vérification, réinitialisation) en français
export const db = getDatabase(app);

/**
 * Émulateurs Firebase (dev local, `npm run emulators`) : REACT_APP_USE_EMULATORS=1.
 * Aucune donnée ne quitte la machine ; les e-mails ne sont pas envoyés (liens visibles
 * dans le journal de l'émulateur), le guard d'e-mail vérifié est désactivé (App.tsx).
 */
export const usingEmulators = process.env.REACT_APP_USE_EMULATORS === "1";
if (usingEmulators) {
  try {
    connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true });
    connectDatabaseEmulator(db, "127.0.0.1", 9000);
  } catch (e) {
    /* déjà connecté (hot reload) */
  }
}

/**
 * App Check (web) : prouve que les requêtes viennent de NOTRE app (reCAPTCHA v3).
 * Activé dès que REACT_APP_APPCHECK_SITE_KEY est renseignée (jamais avec les émulateurs).
 * Le proxy vérifie le jeton (header X-Firebase-AppCheck) quand APP_CHECK_REQUIRED=1.
 * Sur mobile natif : plugin @capacitor-firebase/app-check à ajouter (Play Integrity / App Attest),
 * voir SECURITY.md.
 */
let appCheck: AppCheck | null = null;
const siteKey = process.env.REACT_APP_APPCHECK_SITE_KEY;
if (siteKey && !usingEmulators && typeof window !== "undefined") {
  try {
    if (process.env.REACT_APP_APPCHECK_DEBUG_TOKEN) {
      (window as any).FIREBASE_APPCHECK_DEBUG_TOKEN = process.env.REACT_APP_APPCHECK_DEBUG_TOKEN;
    }
    appCheck = initializeAppCheck(app, { provider: new ReCaptchaV3Provider(siteKey), isTokenAutoRefreshEnabled: true });
  } catch (e) {
    appCheck = null;
  }
}

/** Jeton App Check courant (null si App Check inactif). Ne bloque jamais l'appel. */
export async function getAppCheckToken(): Promise<string | null> {
  if (!appCheck) return null;
  try {
    const res = await getToken(appCheck, false);
    return res.token || null;
  } catch (e) {
    return null;
  }
}

export default app;
